import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { formatDateOrdinal } from './date_util';
import { ExcelButton } from './excel_button';

const ITEMS_PER_PAGE = 50;

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

  // Field IDs:
  // 282 = Location code, 6 = Date of Request, 878 = Status Formula
  // 529 = Job Requester, 284 = Address, 323 = First shift start
  // 324 = Last Shift Start, 538 = Hourly Rate, 200 = Number of Guards
  // 89 = Guard Type, 86 = Armed/Unarmed, 88 = Dress Code
  // 87 = Inside/Outside, 90 = Special Requirements, 133 = Reason

export const ClientRequests = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logoutUser = () => {
    dispatch({ type: 'LOG_OUT', payload: null });
    sessionStorage.setItem('currentUser', null);
    window.location = "/";
  };
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const sortRequests = (requestsList) => {
    return [...requestsList].sort((a, b) => {
      const aDate = a && a[1] && a[1].value ? new Date(a[1].value).getTime() : 0;
      const bDate = b && b[1] && b[1].value ? new Date(b[1].value).getTime() : 0;
      return bDate - aDate;
    });
  };

  const filterRequests = (data, term) => {
    if (!term) return sortRequests(data);
    const filtered = data.filter(item => {
      const locationCode = item && item[282] ? item[282].value : "";
      const address = item && item[284] ? item[284].value : "";
      const jobRequester = item && item[275] ? item[275].value : "";
      const status = item && item[878] ? item[878].value : "";
      return locationCode.toLowerCase().includes(term) ||
        address.toLowerCase().includes(term) ||
        jobRequester.toLowerCase().includes(term) ||
        status.toLowerCase().includes(term);
    });
    return sortRequests(filtered);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchInitial = async () => {
      setIsLoading(true);
      const response = await fetch("/apiclientRequests?limit=50", { credentials: 'include' });
      const data = await response.json();
      if (data && data.sessionExpired) { logoutUser(); return; }
      if (!isMounted) return;
      const sorted = sortRequests(data);
      setRequests(sorted);
      setFilteredRequests(sorted);
      setIsLoading(false);

      // Phase 2: fetch all records in background
      const allResponse = await fetch("/apiclientRequests", { credentials: 'include' });
      const allData = await allResponse.json();
      if (allData && allData.sessionExpired) { logoutUser(); return; }
      if (!isMounted) return;
      const allSorted = sortRequests(allData);
      setRequests(allSorted);
      setFilteredRequests(searchTerm ? filterRequests(allData, searchTerm) : allSorted);
      setIsFullyLoaded(true);
    };

    fetchInitial();
    return () => { isMounted = false; };
  }, []);

  const handleChangeInput = (e) => {
    const term = (e.target.value).toLowerCase();
    setSearchTerm(term);
    setFilteredRequests(filterRequests(requests, term));
    setCurrentPage(1);
  };

  const redirectToRequestPage = (e, requestId) => {
    navigate(`/clientRequest/${requestId}`, {state: {id: requestId}});
  };

  const handleRedirectCreateRequest = () => {
    navigate("/createClientRequest");
  };

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredRequests.length / ITEMS_PER_PAGE));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredRequests, currentPage]);

  const headers = (
    <tr>
      <th>Location Code</th>
      <th>Date of Request</th>
      <th>Status</th>
      <th>Temporary/Recurring</th>
      <th>Address</th>
      <th>First Shift Start</th>
      <th>Last Shift Start</th>
      <th>Number of Guards</th>
      <th>Hourly Rate</th>
    </tr>
  );

  const exportRows = () => filteredRequests.map(r => ({
    'Location Code': r && r[282] ? r[282].value : '',
    'Date of Request': r && r[1] ? formatDateOrdinal(r[1].value) : '',
    'Status': r && r[878] ? r[878].value : '',
    'Temporary/Recurring': r && r[65] ? r[65].value : '',
    'Address': r && r[284] ? r[284].value : '',
    'First Shift Start': r && r[323] ? formatDateOrdinal(r[323].value) : '',
    'Last Shift Start': r && r[324] ? formatDateOrdinal(r[324].value) : '',
    'Number of Guards': r && r[200] ? r[200].value : '',
    'Hourly Rate': r && r[938] ? r[938].value : '',
  }));

  return (
    <div className="tickets">
    <div className="ticketsList">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0 }}>Security Guard Requests</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {/* <button className="mainButton" onClick={() => navigate('/guardRequests/new')} style={{ margin: 0 }}>+ Create Guard Request</button> */}
          <ExcelButton rows={exportRows} fileName="guard-requests.xlsx" sheetName="Guard Requests" />
        </div>
      </div>
      <div className="ticketsActionsWrapper" style={{ marginTop: '16px' }}>
        <div className="searchInputWrapper inputWrapper">
          <input placeholder="Search requests..." onChange={handleChangeInput}></input>
        </div>
      </div>
      {isLoading ? (
        <div className="skeleton-wrapper">
          <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
            Loading requests...
          </p>
          <table className="skeleton-table">
            <tbody>
              {headers}
              {Array.from({ length: 15 }).map((_, idx) => (
                <tr key={idx}>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-long"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
            Showing {filteredRequests.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredRequests.length)} of {filteredRequests.length} requests
            {!isFullyLoaded && <span> (loading more...)</span>}
          </p>
          <table id="tableWrapper">
          <tbody>
          {headers}
            {paginatedRequests && paginatedRequests.length > 0 && paginatedRequests.map((request, idx) => {
              const requestId = request && request[3] ? request[3].value : null;
              const status = request && request[878] ? request[878].value : "";

              // Color palette matches shift status dots
              let statusColor;
              switch(status) {
                case "Complete":
                case "Confirmed":
                  statusColor = "limegreen";
                  break;
                case "Scheduled":
                  statusColor = "#B7966A";
                  break;
                case "Pending Quote":
                case "Unscheduled":
                case "Quoted":
                case "Unconfirmed":
                  statusColor = "orange";
                  break;
                case "Cancelled":
                case "Expired":
                case "Declined":
                  statusColor = "crimson";
                  break;
                default:
                  statusColor = "gray";
              }

              return (
                <tr key={idx} onClick={(e) => redirectToRequestPage(e, requestId)} className="tableRow">
                  <td className="tableCell">{request && request[282] ? request[282].value : ""}</td>
                  <td className="tableCell">{request && request[1] ? formatDateOrdinal(request[1].value) : ""}</td>
                  <td className="tableCell">
                    {status && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                        <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }}></span>
                        {status}
                      </span>
                    )}
                  </td>
                  <td className="tableCell">{request && request[65] ? request[65].value : ""}</td>
                  <td className="tableCell">{request && request[284] ? request[284].value : ""}</td>
                  <td className="tableCell">{request && request[323] ? formatDateOrdinal(request[323].value) : ""}</td>
                  <td className="tableCell">{request && request[324] ? formatDateOrdinal(request[324].value) : ""}</td>
                  <td className="tableCell">{request && request[200] ? request[200].value : ""}</td>
                  <td className="tableCell">{(() => {
                    const rate = request && request[938] ? request[938].value : null;
                    if (!rate || isNaN(parseFloat(rate)) || parseFloat(rate) === 0) return "pending";
                    return `$${parseFloat(rate).toFixed(2)}`;
                  })()}</td>
                </tr>
              )
            })}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="pagination-wrapper">
              <button
                className={`pagination-btn ${currentPage === 1 ? 'pagination-btn--disabled' : ''}`}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              {getPageNumbers(currentPage, totalPages).map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} className="pagination-btn pagination-btn--disabled">...</span>
                ) : (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'pagination-btn--active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                )
              ))}
              <button
                className={`pagination-btn ${currentPage === totalPages ? 'pagination-btn--disabled' : ''}`}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
    </div>
    )
}
